import React, { useMemo } from "react";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import { useParams } from "react-router-dom";

import RealTimeCard from "./RealTimeCard";
import { trailingAverage, useRealtimeSeries } from "./useRealtimeSeries";
import { baseChartOption, lineSeries, metricColor } from "../charts/chartTheme";
import { EM_DASH, describe, formatNumber } from "../../utils/format";

const isValid = (value) => Number.isFinite(value) && value >= 0;

const HeartRateRealTime = () => {
  const { watchId } = useParams();

  const paths = useMemo(
    () => ({
      data: `/Health/${watchId}/realTime/heartRate`,
      status: `/Health/${watchId}/realTime/heartRateStatusDescription`,
      progress: `/Health/${watchId}/realTime/heartRateProgress`,
    }),
    [watchId]
  );

  const { values, samples, status, progress, min, max } = useRealtimeSeries(
    watchId,
    paths,
    isValid
  );

  const stats = useMemo(() => describe(values, 0), [values]);

  const buildOption = () =>
    baseChartOption({
      categories: samples,
      series: [
        lineSeries({
          name: "Heart rate",
          data: values,
          color: metricColor("heartRate"),
          smooth: 0,
          showSymbol: false,
        }),
      ],
      xName: "Sample",
      yName: "bpm",
      yMin: min !== null ? Math.floor(min - 5) : 0,
      yMax: max !== null ? Math.ceil(max + 5) : 100,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${formatNumber(
          point.value,
          0
        )} bpm</strong>`;
      },
    });

  return (
    <RealTimeCard
      title="Live heart rate"
      icon={<FavoriteBorderOutlinedIcon />}
      accent={metricColor("heartRate")}
      exportName="heartRate"
      status={status}
      progress={progress}
      hasData={values.length > 0}
      waitingCopy="The watch is streaming heart rate samples. The trace appears as soon as the first values arrive."
      deps={[values]}
      buildOption={buildOption}
      stats={[
        {
          label: "Latest",
          value: values.length ? formatNumber(values[values.length - 1], 0) : EM_DASH,
          unit: "bpm",
          meta: "Most recent sample",
        },
        {
          label: "Avg (last 30s)",
          value: trailingAverage(values, 30, 0),
          unit: "bpm",
          meta: "Rolling window",
        },
        { label: "Range", value: stats.range, unit: "bpm", meta: "This capture" },
        { label: "Samples", value: values.length || EM_DASH, meta: "Received so far" },
      ]}
    />
  );
};

export default HeartRateRealTime;
